/* SignWarehouse storefront search core (Typesense on search.signwarehouse.com).
 *
 * Shared by the predictive dropdown (sw-search-dropdown.js) and the results page (sw-search-results.js):
 *  - query understanding, mirrored from App\Services\Search\QueryUnderstanding (signwarehouse-app);
 *    shared cases in that repo's search/qu-cases.json
 *  - ranking request, mirrored from SearchRequestBuilder / ScoringFormula; snapshot search/scoring.json
 *  - a Typesense client with a hard timeout so callers can fall back to Shopify's native search
 *  - event beacons (query, click, add-to-cart) to pro3 through the app proxy; purchases are tied to the
 *    session server-side via the cart attribute, never from the browser
 *  - A/B arms (pro3 Experiments, shop metafield sw_search.experiment): a stable hash of the visitor id picks the
 *    arm, the arm's allow-listed parameters override the product search, and every beacon carries exp + variant
 *  - the search host's own analytics: the brand facet call and automated browsers send enable_analytics=false
 *
 * Config comes from snippets/sw-search-config.liquid as window.swSearchConfig. Content rules: popularity is
 * a ranking signal only and is never rendered; availability wording is In stock / Low stock / Sold out, and
 * Drop Shipped when the product's Route metafield is Dropship (the PDP's wording, snippets/product-inventory);
 * one price shown once with the compare-at price struck through beside it.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api; // node tests
  root.SWSearch = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var cfg = (typeof window !== 'undefined' && window.swSearchConfig) || {};

  // ---------------------------------------------------------------- query understanding

  var SKU_TOKEN_RE = /^(?=.*\d)(?=.*[a-z\-\+\/#])[a-z0-9][a-z0-9\-\+\/#\.]{2,}$/i;
  var NUMERIC_CODE_RE = /^\d{3,}(?:[-\/]\d{1,4}){1,3}$/;
  var ORDER_RE = /^#?\d{5,7}$/;
  var PAGE_WORDS = ['return', 'returns', 'refund', 'shipping policy', 'hours', 'warranty', 'tax exempt', 'tax-exempt', 'resale certificate',
    'pro membership', 'pro program', 'privacy', 'terms', 'contact', 'phone number', 'about us', 'careers', 'rebate', 'store credit',
    'financing', 'net terms', 'net 30', 'invoice', 'track order', 'order status'];
  var ORDER_WORDS = ['order', 'my order', 'order number', 'reorder', 'invoice'];

  function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&'); }

  // Pasted text (a whole Google AI Mode chat, an email, a list of SKUs) must never reach the host whole: on
  // 2026-10-02 one 36 KB paste made Typesense allocate 1.8 GB and the 2 GB box killed it. Keep the first
  // 12 words / 100 characters, after dropping chat-transcript labels, so the buyer still gets results.
  var MAX_QUERY_WORDS = 12, MAX_QUERY_CHARS = 100;
  var CHAT_LABEL_RE = /^\s*(?:ai mode conversation|you said|chatgpt said|gemini said|copilot said)\s*:\s*/i;

  function clampQuery(q) {
    q = String(q || '');
    for (var i = 0; i < 4 && CHAT_LABEL_RE.test(q); i++) q = q.replace(CHAT_LABEL_RE, '');
    q = q.replace(/\s+/g, ' ').trim();
    var words = q.split(' ');
    if (words.length > MAX_QUERY_WORDS) q = words.slice(0, MAX_QUERY_WORDS).join(' ');
    if (q.length > MAX_QUERY_CHARS) {
      q = q.slice(0, MAX_QUERY_CHARS);
      var cut = q.lastIndexOf(' ');
      if (cut > MAX_QUERY_CHARS / 2) q = q.slice(0, cut);
    }
    return q;
  }

  function normalize(q) {
    // (mirror of QueryUnderstanding::normalize; quotes not following a digit are plain text, not inches)
    q = String(q || '').toLowerCase().trim();
    q = q.replace(/[“”″＂]/g, '"').replace(/[’′]/g, "'");
    q = q.replace(/\s+/g, ' ');
    q = q.replace(/\b(in|ft|yd|yds)\.(?=\s|$)/g, '$1');
    q = q.replace(/(?<!\d)"/g, ' ').replace(/\s+/g, ' ').trim();
    return joinGenerations(q).trim();
  }

  /** "gen 2", "generation 2", "2nd gen" → "gen2" (mirror of QueryUnderstanding::joinGenerations; the index adds the joined form of spaced titles). */
  function joinGenerations(text) {
    return text.replace(/(?<![a-z0-9])(?:(\d{1,2})(?:st|nd|rd|th)[\s\-]*gen(?:eration)?|gen(?:eration)?\.?[\s\-]*(\d{1,2}))(?![a-z0-9])(?!\s*(?:"|in\b|inch|ft\b|feet|foot|yds?\b|yards?\b|mm\b|cm\b|x\s*\d))/g, function (all, a, b) {
      return 'gen' + (a || b);
    });
  }

  function numStr(v) { return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); }

  /** brands: lower-cased dictionary (longest first is handled here). */
  /** "oracal751" → "oracal 751": glued brand+model tokens (4+ letters, 2–5 digits, ≤2 letters); part numbers with dashes stay whole. */
  function splitGluedModels(text) {
    return text.replace(/(?<![a-z0-9\-.\/+#])([a-z]{4,})(\d{2,5})([a-z]{0,2})(?![a-z0-9\-.\/+#])/g, function (all, a, b, c) {
      if (['inch', 'yard', 'yards', 'feet', 'foot'].indexOf(a) !== -1) return all;
      return a + ' ' + b + (c ? ' ' + c : '');
    });
  }

  function analyze(query, brands) {
    var normalized = normalize(clampQuery(query));
    var rawTokens = normalized === '' ? [] : normalized.split(/\s+/);
    var residual = splitGluedModels(normalized);
    var width = null, length = null, m;

    m = residual.match(/(?<![\d.\/])(\d{1,3}(?:\.\d+)?)\s*(?:"|in\b|inch(?:es)?\b|-in\b)/);
    if (m) {
      var v = parseFloat(m[1]);
      if (v >= 0.5 && v <= 120) { width = Math.round(v * 100) / 100; residual = residual.replace(m[0], ' ').trim(); }
    } else {
      m = residual.match(/(?<![\d.])(\d{1,3}(?:\.\d+)?)\s*(?:ft\b|feet\b|foot\b)/);
      if (m) {
        var v2 = parseFloat(m[1]) * 12;
        if (v2 >= 0.5 && v2 <= 120) { width = Math.round(v2 * 100) / 100; residual = residual.replace(m[0], ' ').trim(); }
      }
    }
    m = residual.match(/(?<![\d.])(\d{1,4}(?:\.\d+)?)\s*(?:yds?\b|yards?\b|yrds?\b|y\b)/);
    if (m) {
      var v3 = parseFloat(m[1]);
      if (v3 >= 1 && v3 <= 1000) { length = Math.round(v3 * 100) / 100; residual = residual.replace(m[0], ' ').trim(); }
    }
    if (width !== null || length !== null) residual = residual.replace(/(^|\s)(x|by)(?=\s|$)/g, ' '); // dangling dimension separator only
    // "6x12" without units is a blank/sheet size: text the titles use, never a filter, never a part number
    var sheetSize = false;
    if (width === null && length === null) {
      var rewritten = residual.replace(/(?<![\d.])(\d{1,3})\s*x\s*(\d{1,3})(?![\d."])/g, '$1 in x $2 in');
      sheetSize = rewritten !== residual; residual = rewritten;
    }
    residual = residual.replace(/\s+/g, ' ').trim();
    var tokens = residual === '' ? [] : residual.split(/\s+/);

    var brand = null;
    var dict = (brands || []).map(function (b) { return String(b).toLowerCase().trim(); }).filter(Boolean).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < dict.length; i++) {
      if (new RegExp('(?<![a-z0-9])' + escapeRe(dict[i]) + '(?![a-z0-9])').test(residual)) { brand = dict[i]; break; }
    }

    var skuShape = false, skuCompact = null;
    var skuTokens = rawTokens.length && rawTokens.length <= 2 ? rawTokens : tokens; // judged on the buyer's own tokens
    if (!sheetSize && skuTokens.length && skuTokens.length <= 2) {
      var joined = skuTokens.join('');
      skuShape = skuTokens.every(function (t) { return SKU_TOKEN_RE.test(t) || NUMERIC_CODE_RE.test(t); }) && joined.length >= 4;
      if (skuShape) {
        skuCompact = joined.replace(/[^a-z0-9]/g, '');
        if (brand !== null && residual.indexOf(brand) !== -1) { skuShape = false; skuCompact = null; }
      }
    }

    return {
      original: query, normalized: normalized, residual: residual, tokens: tokens,
      sku_shape: skuShape, sku_compact: skuCompact, width_in: width, length_yd: length, brand: brand,
      intent: intent(normalized, skuShape), empty: normalized === ''
    };
  }

  function intent(normalized, skuShape) {
    if (normalized === '') return 'product';
    if (ORDER_RE.test(normalized)) return 'order';
    for (var i = 0; i < ORDER_WORDS.length; i++) {
      var w = ORDER_WORDS[i];
      if (normalized === w || (normalized.indexOf(w + ' ') === 0 && /#?\d{4,}/.test(normalized))) return 'order';
    }
    if (!skuShape) {
      for (var j = 0; j < PAGE_WORDS.length; j++) {
        if (new RegExp('(?<![a-z])' + escapeRe(PAGE_WORDS[j]) + '(?![a-z])').test(normalized)) return 'page';
      }
    }
    return 'product';
  }

  function dimensionFilters(a) {
    var f = [];
    if (a.width_in !== null && a.width_in !== undefined) f.push('width_in:=' + numStr(a.width_in));
    if (a.length_yd !== null && a.length_yd !== undefined) f.push('length_yd:=' + numStr(a.length_yd));
    return f;
  }

  // ---------------------------------------------------------------- ranking (mirror of ScoringFormula / SearchRequestBuilder)

  var WEIGHTS = { purchased: 1, not_clearance: 8, in_stock: 4, orders_90d_50: 3, orders_90d_10: 2, orders_90d_1: 1, behavior_5: 2, behavior_1: 1, margin_2: 1, recent: 1 };
  var QUERY_BY = { fields: ['title', 'skus', 'skus_compact', 'barcodes', 'brand', 'search_keywords', 'variant_titles', 'option_values', 'categories', 'product_type', 'tag_values', 'vendor', 'description'], weights: [10, 10, 9, 9, 7, 7, 5, 5, 4, 4, 3, 3, 1] };
  var SKU_QUERY_BY = { fields: ['skus', 'skus_compact', 'barcodes', 'title'], weights: [10, 10, 10, 2] };

  /** Mirrors ScoringFormula::sortBy. rank_score is summed at index time; see search/scoring.json in the app repo. */
  function buildSortBy(purchasedIds, now) {
    var text = '_text_match:desc'; // strict relevance; rank_score only breaks exact ties (no buckets since 2026-09-22)
    var ids = (purchasedIds || []).map(function (id) { return String(id).replace(/\D/g, ''); }).filter(Boolean).slice(0, 200);
    if (ids.length) return text + ',_eval([(id:[' + ids.join(',') + ']):' + WEIGHTS.purchased + ']):desc,rank_score:desc';
    return text + ',rank_score:desc,popularity:desc';
  }

  // ---------------------------------------------------------------- A/B experiments

  /** FNV-1a 32-bit hash of a string, as a number in [0, 1). Stable across page loads and browsers. */
  function hashUnit(str) {
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h / 4294967296;
  }
  /**
   * The visitor's arm in the running experiment, or null (no experiment, or outside its traffic share).
   * exp = {active, id, traffic_pct, variants: [{key, weight, params}]}; the first variant is the control.
   */
  function assignArm(exp, sid) {
    if (!exp || !exp.active || !exp.id || !exp.variants || exp.variants.length < 2 || !sid) return null;
    if (hashUnit(sid + ':' + exp.id + ':in') * 100 >= Number(exp.traffic_pct || 0)) return null;
    var total = 0;
    exp.variants.forEach(function (v) { total += Math.max(1, Number(v.weight) || 1); });
    var x = hashUnit(sid + ':' + exp.id + ':arm') * total;
    for (var i = 0; i < exp.variants.length; i++) {
      x -= Math.max(1, Number(exp.variants[i].weight) || 1);
      if (x < 0) return { id: Number(exp.id), key: String(exp.variants[i].key), params: exp.variants[i].params || {} };
    }
    var last = exp.variants[exp.variants.length - 1];
    return { id: Number(exp.id), key: String(last.key), params: last.params || {} };
  }
  var armMemo;
  function experimentArm() {
    if (armMemo === undefined) armMemo = assignArm(cfg.experiment, sessionId());
    return armMemo;
  }
  /** Apply an arm's allow-listed overrides (pro3 Experiments::PARAMS) to a product search. SKU passes are untouched. */
  function applyArm(params, arm, ctx) {
    if (!arm || !arm.params || ctx.skuPass) return params;
    var p = arm.params;
    if (p.num_typos !== undefined && !ctx.relaxed) params.num_typos = String(p.num_typos);
    if (p.prefix !== undefined) params.prefix = p.prefix ? 'true' : 'false';
    if (p.prioritize_exact_match !== undefined) params.prioritize_exact_match = p.prioritize_exact_match ? 'true' : 'false';
    if (p.drop_tokens_threshold !== undefined && !ctx.relaxed) params.drop_tokens_threshold = String(p.drop_tokens_threshold);
    if (p.typo_tokens_threshold !== undefined) params.typo_tokens_threshold = String(p.typo_tokens_threshold);
    if (p.query_by_weights && p.query_by_weights.length === QUERY_BY.weights.length) params.query_by_weights = p.query_by_weights.join(',');
    if (p.sort === 'relevance_only') {
      var ids = (ctx.purchased || []).map(function (id) { return String(id).replace(/\D/g, ''); }).filter(Boolean).slice(0, 200);
      params.sort_by = '_text_match:desc' + (ids.length ? ',_eval([(id:[' + ids.join(',') + ']):' + WEIGHTS.purchased + ']):desc' : '');
    }
    return params;
  }

  /** opts: per_page, page, purchased, filters[], facets, sku_pass, relaxed, hybrid, arm (null = none; default: the visitor's) */
  function buildProductSearch(a, opts) {
    opts = opts || {};
    var skuPass = !!opts.sku_pass, relaxed = !!opts.relaxed;
    var hybrid = opts.hybrid !== undefined ? !!opts.hybrid : !!cfg.hybrid;
    var dimensionOnly = a.residual === '' && ((a.width_in !== null && a.width_in !== undefined) || (a.length_yd !== null && a.length_yd !== undefined));
    var q = skuPass ? (a.sku_compact || a.residual) : (a.residual !== '' ? a.residual : (dimensionOnly ? '*' : a.normalized));
    if (!q) q = '*';
    var qb = skuPass ? SKU_QUERY_BY : QUERY_BY;
    var params = {
      q: q,
      query_by: qb.fields.join(','),
      query_by_weights: qb.weights.join(','),
      infix: qb.fields.map(function (f) {
        if (f === 'skus' || f === 'skus_compact' || f === 'barcodes') return 'fallback';
        if (f === 'title') return skuPass ? 'fallback' : 'off';
        return 'off';
      }).join(','),
      prefix: 'true',
      prioritize_exact_match: 'true',
      prioritize_num_matching_fields: 'false',
      num_typos: skuPass ? '0' : (relaxed ? '2' : '1'),
      typo_tokens_threshold: '1',
      drop_tokens_threshold: relaxed ? '1' : '0',
      min_len_1typo: '5',
      min_len_2typo: '8',
      sort_by: buildSortBy(opts.purchased || [], opts.now),
      per_page: String(opts.per_page || 24),
      page: String(opts.page || 1),
      exclude_fields: 'description,embedding',
      highlight_fields: 'title,skus',
      enable_overrides: 'true'
    };
    var filters = (opts.filters || []).filter(Boolean);
    if (!skuPass && !relaxed) {
      var dims = dimensionFilters(a);
      if (opts.drop_length) dims = dims.filter(function (f) { return f.indexOf('length_yd') !== 0; });
      if (opts.width_range && a.width_in !== null && a.width_in !== undefined) dims = ['width_in:[' + numStr(Math.round(a.width_in * 75) / 100) + '..' + numStr(Math.round(a.width_in * 125) / 100) + ']'];
      filters = filters.concat(dims);
    }
    if (filters.length) params.filter_by = filters.join(' && ');
    if (opts.facets) { params.facet_by = 'brand,width_label,finish,color,compatibility,categories,availability,price_min'; params.max_facet_values = '30'; }
    if (hybrid && !skuPass && q !== '*') params.vector_query = 'embedding:([], alpha: ' + (cfg.hybridAlpha || 0.7) + ')';
    if (automated()) params.enable_analytics = 'false';
    return applyArm(params, opts.arm !== undefined ? opts.arm : experimentArm(), { skuPass: skuPass, relaxed: relaxed, purchased: opts.purchased || [] });
  }

  function buildCollectionSearch(a, perPage, withDescription) {
    var q = a.residual !== '' ? a.residual : a.normalized;
    var p = { q: q || '*', query_by: 'title,description', query_by_weights: '10,2', num_typos: '1', per_page: String(perPage || 4), sort_by: '_text_match:desc,is_hub:desc,products_count:desc' };
    if (!withDescription) p.exclude_fields = 'description';
    return p;
  }

  /** Query completions from pro3's nightly query log (collection `suggestions`, may not exist yet). */
  function buildSuggestionSearch(a, perPage) {
    return { q: a.normalized || '*', query_by: 'q', num_typos: '1', prefix: 'true', per_page: String(perPage || 5), sort_by: '_text_match:desc,count:desc', include_fields: 'q,count' };
  }

  function buildPageSearch(a, perPage) {
    return { q: a.normalized || '*', query_by: 'title,body', query_by_weights: '10,1', num_typos: '1', per_page: String(perPage || 3), exclude_fields: 'body' };
  }

  // ---------------------------------------------------------------- Typesense client

  function configured() {
    return !!(cfg.host && cfg.key && typeof fetch === 'function');
  }

  /** searches: [{collection, ...params}] → array of results. Rejects on timeout / HTTP error. */
  /** Opens the TLS connection to the search host ahead of the first query (called on search-box focus). */
  var warmed = false;
  function warm() {
    if (warmed || !cfg.host || typeof fetch !== 'function') return;
    warmed = true;
    try { fetch(cfg.host.replace(/\/$/, '') + '/health', { mode: 'cors', cache: 'no-store', keepalive: true }).catch(function () { /* fallback covers it */ }); } catch (e) { /* ignore */ }
  }

  function multiSearch(searches, timeoutMs) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, timeoutMs || cfg.timeoutMs || 300);
    var url = String(cfg.host).replace(/\/$/, '') + '/multi_search?x-typesense-api-key=' + encodeURIComponent(cfg.key) + (automated() ? '&enable_analytics=false' : '');
    return fetch(url, {
      method: 'POST', mode: 'cors', credentials: 'omit', signal: ctl ? ctl.signal : undefined,
      headers: { 'Content-Type': 'text/plain' }, // no preflight
      body: JSON.stringify({ searches: searches })
    }).then(function (r) {
      if (!r.ok) throw new Error('search host ' + r.status);
      return r.json();
    }).then(function (json) {
      var results = json.results || [];
      results.forEach(function (res, i) {
        if (res && res.error) {
          // an optional collection (suggestions) may not exist yet; only the product search is fatal
          if (searches[i] && searches[i].collection === cfg.collections.products) throw new Error(res.error);
          results[i] = { hits: [], found: 0 };
        }
      });
      return results;
    }).finally(function () { clearTimeout(timer); });
  }

  var brandCache = null;
  function brands() {
    if (brandCache) return Promise.resolve(brandCache);
    try {
      var raw = sessionStorage.getItem('sw_search_brands');
      if (raw) { var j = JSON.parse(raw); if (j && j.t > Date.now() - 3600e3) { brandCache = j.v; return Promise.resolve(brandCache); } }
    } catch (e) { /* storage unavailable */ }
    // enable_analytics false: this facet read is not a buyer's search and must not reach the host's query counts
    return multiSearch([{ collection: cfg.collections.products, q: '*', facet_by: 'brand,vendor', max_facet_values: '250', per_page: '0', enable_analytics: false }], 1500).then(function (res) {
      var out = {};
      ((res[0] && res[0].facet_counts) || []).forEach(function (f) { (f.counts || []).forEach(function (c) { var v = String(c.value || '').toLowerCase().trim(); if (v.length >= 2) out[v] = 1; }); });
      brandCache = Object.keys(out);
      try { sessionStorage.setItem('sw_search_brands', JSON.stringify({ t: Date.now(), v: brandCache })); } catch (e) { /* ignore */ }
      return brandCache;
    }).catch(function () { return []; });
  }

  // purchase-history boost: signed-in customers with orders only; ids come from pro3 via the app proxy
  var historyPromise = null;
  function purchasedIds() {
    if (!cfg.customer || !cfg.customer.loggedIn || !cfg.customer.hasOrders || !cfg.eventsPath) return Promise.resolve([]);
    if (historyPromise) return historyPromise;
    try {
      var raw = sessionStorage.getItem('sw_search_history');
      if (raw) { var j = JSON.parse(raw); if (j && j.t > Date.now()) { historyPromise = Promise.resolve(j.v); return historyPromise; } }
    } catch (e) { /* ignore */ }
    historyPromise = fetch(cfg.eventsPath.replace(/\/$/, '') + '/history', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: '{}' })
      .then(function (r) { return r.ok ? r.json() : { product_ids: [], ttl: 0 }; })
      .then(function (j) {
        var ids = (j.product_ids || []).map(String);
        try { sessionStorage.setItem('sw_search_history', JSON.stringify({ t: Date.now() + Math.max(60, j.ttl || 0) * 1000, v: ids })); } catch (e) { /* ignore */ }
        return ids;
      }).catch(function () { return []; });
    return historyPromise;
  }

  /**
   * The full pipeline in as few round trips as possible:
   *   request 1: [sku pass if it looks like one] + full-text + collections (+ pages when intent=page)
   *   request 2 (only when nothing was found): relaxed full-text
   * Resolves {analysis, pass, products, collections, pages, redirect, found, tookMs}. Rejects on host failure.
   */
  function search(query, opts) {
    opts = opts || {};
    var started = Date.now();
    return Promise.all([brands(), opts.skipHistory ? [] : purchasedIds()]).then(function (pre) {
      var a = analyze(query, pre[0]);
      var purchased = pre[1];
      var base = { per_page: opts.per_page || 8, page: opts.page || 1, purchased: purchased, filters: opts.filters || [], facets: !!opts.facets, hybrid: opts.hybrid };
      var searches = [];
      var idx = {};
      if (a.sku_shape) { idx.sku = searches.length; searches.push(Object.assign({ collection: cfg.collections.products }, buildProductSearch(a, Object.assign({}, base, { sku_pass: true })))); }
      idx.full = searches.length; searches.push(Object.assign({ collection: cfg.collections.products }, buildProductSearch(a, base)));
      idx.coll = searches.length; searches.push(Object.assign({ collection: cfg.collections.collections }, buildCollectionSearch(a, opts.collections_per_page || 4, !!opts.collectionDescription)));
      if (a.intent === 'page' && cfg.collections.pages) { idx.pages = searches.length; searches.push(Object.assign({ collection: cfg.collections.pages }, buildPageSearch(a, 3))); }
      if (opts.suggestions && cfg.collections.suggestions) { idx.sugg = searches.length; searches.push(Object.assign({ collection: cfg.collections.suggestions }, buildSuggestionSearch(a, opts.suggestions_per_page || 5))); }

      // One retry on any failure, with a longer budget: the host answers in <50 ms (p99, Caddy log 2026-09-26),
      // so a miss is the network (cold connection, mobile, far-away Cloudflare colo), and a second try almost
      // always lands. Native search is for a host that is really down, not a slow round trip. The retry also
      // drops suggestions: a scoped key answers 401 for the whole batch when one collection does not exist yet.
      var retryMs = opts.retryMs || Math.max((opts.timeoutMs || cfg.timeoutMs || 300) * 3, 1500);
      var run = multiSearch(searches, opts.timeoutMs).catch(function () {
        var without = idx.sugg === undefined ? searches : searches.filter(function (_, i) { return i !== idx.sugg; });
        idx.sugg = undefined;
        return multiSearch(without, retryMs);
      });
      return run.then(function (res) {
        var pass = 'full', products = res[idx.full];
        if (idx.sku !== undefined && res[idx.sku] && (res[idx.sku].found || 0) > 0) { pass = 'sku'; products = res[idx.sku]; }
        var out = { analysis: a, pass: pass, products: products, collections: res[idx.coll] || { hits: [] }, pages: idx.pages !== undefined ? res[idx.pages] : { hits: [] }, suggestions: idx.sugg !== undefined ? res[idx.sugg] : { hits: [] }, purchased: purchased, request: searches[idx.full] };
        var fallbacks = [];
        if ((products.found || 0) === 0 && a.width_in !== null && a.width_in !== undefined && a.length_yd !== null && a.length_yd !== undefined) fallbacks.push(['width', { drop_length: true }]);
        if ((products.found || 0) === 0 && a.width_in !== null && a.width_in !== undefined) fallbacks.push(['width_range', { drop_length: true, width_range: true }]);
        if ((products.found || 0) === 0 && a.residual !== '' && !opts.noRelax) fallbacks.push(['relaxed', { relaxed: true }]);
        var chain = Promise.resolve(out);
        fallbacks.forEach(function (fb) {
          chain = chain.then(function (o) {
            if ((o.products.found || 0) > 0) return o;
            var req = Object.assign({ collection: cfg.collections.products }, buildProductSearch(a, Object.assign({}, base, fb[1])));
            // a slow relaxed pass keeps the answer we already have (zero results) instead of failing the search
            return multiSearch([req], retryMs).then(function (r2) { o.products = r2[0]; o.pass = fb[0]; o.request = req; return o; }, function () { return o; });
          });
        });
        return chain;
      }).then(function (out) {
        out.found = out.products.found || 0;
        out.redirect = (out.products.metadata && out.products.metadata.redirect) || null;
        // merchandising slot from a curation rule (RulesSync metadata.promo): rendered by the results page
        out.promo = (out.products.metadata && out.products.metadata.promo && (out.products.metadata.promo.title || out.products.metadata.promo.image_url)) ? out.products.metadata.promo : null;
        out.tookMs = Date.now() - started;
        return out;
      });
    });
  }

  // ---------------------------------------------------------------- events (beacons to pro3 via the app proxy)

  function uid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
    var s = ''; while (s.length < 32) s += Math.random().toString(36).slice(2); return s.slice(0, 32);
  }
  function sessionId() {
    var sid = null;
    try {
      var raw = localStorage.getItem('sw_sid');
      if (raw) { var j = JSON.parse(raw); if (j && j.t > Date.now()) sid = j.v; }
      if (!sid) { sid = uid(); localStorage.setItem('sw_sid', JSON.stringify({ v: sid, t: Date.now() + 30 * 86400e3 })); }
    } catch (e) { sid = sid || uid(); }
    return sid;
  }
  var queue = [];
  var flushTimer = null;
  /** Automated browsers (Playwright, CI runners, scrapers that set it) must not write to the query log. */
  function automated() { try { return typeof navigator !== 'undefined' && navigator.webdriver === true; } catch (e) { return false; } }

  function enqueue(ev) {
    if (automated()) return;
    if (!cfg.eventsPath) return;
    queue.push(ev);
    if (queue.length >= 20) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, 2000);
  }
  function flush() {
    clearTimeout(flushTimer); flushTimer = null;
    if (!queue.length || !cfg.eventsPath) return;
    var batch = queue.splice(0, 25);
    var body = JSON.stringify({ sid: sessionId(), events: batch });
    var url = cfg.eventsPath.replace(/\/$/, '') + '/events';
    var sent = false;
    if (navigator.sendBeacon) { try { sent = navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' })); } catch (e) { sent = false; } }
    if (!sent) fetch(url, { method: 'POST', keepalive: true, credentials: 'same-origin', headers: { 'Content-Type': 'text/plain' }, body: body }).catch(function () { /* best effort */ });
    if (queue.length) flushTimer = setTimeout(flush, 500);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
    // Add-to-cart after a search click (dropdown → product page → add): the theme fires on:cart:add with the
    // variant id; the product id comes from Shopify's page meta. Only within 30 minutes of the click.
    document.addEventListener('on:cart:add', function (evt) {
      try {
        var raw = sessionStorage.getItem('sw_last_click');
        if (!raw) return;
        var last = JSON.parse(raw);
        if (!last || last.t < Date.now() - 30 * 60e3) return;
        var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) || null;
        var pid = meta && meta.id ? String(meta.id) : last.product_id;
        if (meta && meta.id && String(meta.id) !== String(last.product_id)) return; // added a different product
        logCart(last.qid, pid, evt.detail && evt.detail.variantId, 1);
        flush();
      } catch (e) { /* ignore */ }
    });
  }

  /** Marks the cart with the session id once, so pro3 can attribute the eventual order (never trusts the browser for identity). */
  var stamped = false;
  function stampCart() {
    if (stamped || !cfg.cartAttribute) return;
    stamped = true;
    try { if (sessionStorage.getItem('sw_cart_stamped') === sessionId()) return; } catch (e) { /* ignore */ }
    var attrs = {}; attrs[cfg.cartAttribute] = sessionId();
    fetch((cfg.routes && cfg.routes.cartUpdate) || '/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: attrs }) })
      .then(function () { try { sessionStorage.setItem('sw_cart_stamped', sessionId()); } catch (e) { /* ignore */ } })
      .catch(function () { stamped = false; });
  }

  var lastQid = null;
  function logQuery(query, result, source, engine) {
    var qid = uid();
    lastQid = qid;
    var ids = ((result && result.products && result.products.hits) || []).slice(0, 20).map(function (h) { return String(h.document.id); });
    var arm = experimentArm();
    // result_count null = unknown: a native fallback never reached the search host, so it is not a zero result
    enqueue({ type: 'query', qid: qid, q: String(query).slice(0, 255), source: source || 'page', engine: engine || 'typesense', intent: result && result.analysis ? result.analysis.intent : null,
      result_count: result ? (result.found || 0) : null, result_ids: ids, took_ms: result ? result.tookMs : null,
      exp: arm ? arm.id : null, variant: arm ? arm.key : null });
    stampCart();
    return qid;
  }
  function logClick(qid, productId, position, variantId) {
    enqueue({ type: 'click', qid: qid || lastQid, product_id: String(productId), position: position, variant_id: variantId ? String(variantId) : null });
    try { sessionStorage.setItem('sw_last_click', JSON.stringify({ qid: qid || lastQid, product_id: String(productId), t: Date.now() })); } catch (e) { /* ignore */ }
    flush();
  }
  function logCart(qid, productId, variantId, quantity) {
    enqueue({ type: 'cart', qid: qid || lastQid, product_id: String(productId), variant_id: variantId ? String(variantId) : null, quantity: quantity || 1 });
  }

  /**
   * Query completions for the dropdown, leader-style ("socks" → "socks for women", "compression socks").
   * Sources, in order: pro3's query log (suggestions collection, once search is live), collection titles that
   * contain the typed words, brands that start with the typed text. Deduplicated, max `limit`.
   * Each item: {text, url}. Collection completions go to the collection page; the rest run a search.
   */
  function completions(result, limit) {
    var a = result.analysis, q = (a.residual || a.normalized || '').trim();
    if (!q) return [];
    var seen = {}, out = [];
    function add(text, url) {
      var key = String(text).toLowerCase().trim();
      if (!key || key === q || seen[key]) return;
      seen[key] = 1; out.push({ text: text, url: url });
    }
    ((result.suggestions && result.suggestions.hits) || []).forEach(function (h) { add(h.document.q, (cfg.routes && cfg.routes.search || '/search') + '?q=' + encodeURIComponent(h.document.q)); });
    var qt = q.split(' ');
    ((result.collections && result.collections.hits) || []).forEach(function (h) {
      var t = String(h.document.title || ''), tl = t.toLowerCase();
      if (qt.every(function (w) { return tl.indexOf(w) !== -1; })) add(t, h.document.url);
    });
    (brandCache || []).forEach(function (b) { if (b.indexOf(q) === 0 && b !== q) add(b, (cfg.routes && cfg.routes.search || '/search') + '?q=' + encodeURIComponent(b)); });
    return out.slice(0, limit || 5);
  }

  // ---------------------------------------------------------------- presentation helpers (content rules)

  function money(amount) {
    var n = Number(amount || 0);
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD' }).format(n); } catch (e) { return '$' + n.toFixed(2); }
  }
  /** The PDP's own states: "Drop Shipped" when the Route metafield is Dropship (it wins over stock, as on the PDP). */
  function availabilityLabel(doc) {
    var s = cfg.strings || {};
    if (doc.availability === 'drop_shipped') return s.dropShipped || 'Drop Shipped';
    if (doc.availability === 'low_stock') return s.lowStock || 'Low stock';
    if (doc.availability === 'sold_out' || doc.in_stock === false) return s.soldOut || 'Sold out';
    return s.inStock || 'In stock';
  }
  /** One price shown once; compare-at struck through beside it when present. "From" when variants differ. Nothing when the theme hides the price. */
  function priceHtml(doc) {
    if (doc.hide_price) return '';
    var s = cfg.strings || {};
    var from = doc.price_max > doc.price_min ? '<span class="sw-price__from price__from">' + (s.from || 'From') + '</span> ' : '';
    var html = from + '<strong class="sw-price__amount price__current">' + money(doc.price_min) + '</strong>';
    if (doc.compare_at_min && doc.compare_at_min > doc.price_min) html += ' <s class="sw-price__compare price__was">' + money(doc.compare_at_min) + '</s>';
    return '<span class="sw-price">' + html + '</span>';
  }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  /** Typesense highlight snippet (already escaped by the server with <mark>) or the escaped plain value. */
  function highlight(hit, field, fallback) {
    var h = hit && hit.highlight && hit.highlight[field];
    if (h && h.snippet) return h.snippet;
    if (h && h.snippets && h.snippets.length) return h.snippets[0];
    return escapeHtml(fallback);
  }
  function matchedSku(hit) {
    var h = hit && hit.highlight && (hit.highlight.skus || hit.highlight.skus_compact);
    if (h && h.snippets && h.snippets.length) return h.snippets[0];
    return null;
  }
  /**
   * Phase 3 reranker (pro3 POST /apps/pro/search/rerank), results page only, when the theme setting is on.
   * Resolves to the reranked product ids, or null when it is off, slow, or failed; never rejects.
   */
  function rerank(query, timeoutMs) {
    if (!cfg.rerank || !cfg.eventsPath || automated()) return Promise.resolve(null);
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, timeoutMs || 350);
    return fetch(cfg.eventsPath.replace(/\/$/, '') + '/rerank', { method: 'POST', credentials: 'same-origin', signal: ctl ? ctl.signal : undefined, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ q: String(query).slice(0, 200) }) })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return j && j.reranked && j.ids && j.ids.length ? j.ids.map(String) : null; })
      .catch(function () { return null; })
      .finally(function () { clearTimeout(timer); });
  }
  /** Reorder items (Typesense hits or InstantSearch items with objectID/id) by a reranked id list; others keep their order after. */
  function reorderByIds(items, ids) {
    if (!ids || !ids.length) return items;
    var rank = {};
    ids.forEach(function (id, i) { rank[String(id)] = i; });
    return items.map(function (it, i) { var id = String(it.objectID || it.id || (it.document && it.document.id) || ''); return { it: it, r: rank[id] !== undefined ? rank[id] : ids.length + i }; })
      .sort(function (x, y) { return x.r - y.r; }).map(function (x) { return x.it; });
  }

  function imageUrl(url, width) {
    if (!url) return null;
    if (/cdn\.shopify\.com/.test(url) && !/[?&]width=/.test(url)) return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || 160);
    return url;
  }

  return {
    cfg: cfg, configured: configured, clampQuery: clampQuery, normalize: normalize, analyze: analyze, dimensionFilters: dimensionFilters,
    buildSortBy: buildSortBy, buildProductSearch: buildProductSearch, buildCollectionSearch: buildCollectionSearch, buildPageSearch: buildPageSearch,
    multiSearch: multiSearch, warm: warm, brands: brands, purchasedIds: purchasedIds, search: search, completions: completions, buildSuggestionSearch: buildSuggestionSearch,
    sessionId: sessionId, logQuery: logQuery, logClick: logClick, logCart: logCart, flush: flush,
    assignArm: assignArm, experimentArm: experimentArm, applyArm: applyArm, hashUnit: hashUnit, rerank: rerank, reorderByIds: reorderByIds,
    money: money, availabilityLabel: availabilityLabel, priceHtml: priceHtml, escapeHtml: escapeHtml, highlight: highlight, matchedSku: matchedSku, imageUrl: imageUrl,
    WEIGHTS: WEIGHTS, QUERY_BY: QUERY_BY, SKU_QUERY_BY: SKU_QUERY_BY
  };
});
